import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { deleteExperimentMutation } from 'api/mutations/experiments';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useExperimentsTranslations } from '../useTranslations';

type CallbackFn = () => void;

type DeleteExperiment = {
  id: number;
  name: string;
  showSuccessToaster?: boolean;
  /** Do the same action whether the operation is a success or not */
  onComplete?: CallbackFn;
  onSuccess?: CallbackFn;
  onError?: CallbackFn;
};

const source = 'useDeleteExperiment';

export const useDeleteExperiment = ({
  id,
  name,
  showSuccessToaster = true,
  onComplete,
  onSuccess,
  onError,
}: DeleteExperiment) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const onSuccessCallback = onSuccess || onComplete;
  const onErrorCallback = onError || onComplete;
  const errorMessage = `Failed to delete experiment id ${id}`;
  const errorToasterMessage = t('errorMessage.deleteExperiment');
  const successToasterMessage = t('successMessage.deleteExperiment', { experiment: name });

  const errorHandler = () => {
    if (onErrorCallback) onErrorCallback();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage: errorToasterMessage,
          source,
          error: { message: errorMessage },
        }),
      ),
    );
  };

  const [deleteExperiment] = useMutation(deleteExperimentMutation, {
    onError: errorHandler,
  });

  return async () => {
    const { data } = await deleteExperiment({
      variables: { id },
    });

    if (data?.deleteExperiment?.id) {
      if (onSuccessCallback) onSuccessCallback();
      if (showSuccessToaster) {
        dispatch(addPositiveToast(successToasterMessage));
      }
    }

    if (data?.deleteExperiment?.message) {
      errorHandler();
    }
  };
};
